import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, LoaderIcon, AlertCircleIcon, UsersIcon } from 'lucide-react';
import { degreeCategories, degreeInterestOptions, focusOptions, getSpecializationsForFocus } from '../data/degrees';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { TrustSignals } from '../components/TrustSignals';
import { useFormState } from '../hooks/useFormState';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { HomeInlineLeadCapture } from '../components/HomeInlineLeadCapture';
import { usePageMeta } from '../hooks/usePageMeta';
import { readMatchParams, resolveMatchPrefill, buildMatchHeadline, readCopyOverrides } from '../utils/matchParams';
export function DegreeListingPage() {
  const {
    category
  } = useParams<{
    category: string;
  }>();
  const navigate = useNavigate();
  const categoryData = degreeCategories.find(c => c.id === category);
  // Anchor and campaign links carry the visitor's context
  // (?degreeInterest=&areaStudy=&mProgram=), which turns this page into a
  // program-specific landing page: it drives the widget, the headline, and the
  // school listings so all three agree. Resolved once — the URL doesn't change
  // while the page is open.
  const [prefill] = useState(() => resolveMatchPrefill(readMatchParams()));
  const [copy] = useState(() => readCopyOverrides());
  const headline = buildMatchHeadline(prefill, categoryData?.name || 'Degree', copy.headline);
  const subhead = copy.subhead || (prefill.programLabel ? `Compare accredited online ${prefill.programLabel.toLowerCase()} programs and find the right fit for your career goals.` : `${categoryData?.description || ''} Compare top accredited programs and find the right fit for your career goals.`);
  usePageMeta(`${headline} | OnlineCollegeGuide`, `Compare accredited online ${(prefill.programLabel || categoryData?.name || 'degree').toLowerCase()} programs. Get matched free by career goals, schedule, and budget.`, {
    ogImage: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    formData,
    errors,
    updateFormData,
    validateHeroForm
  } = useFormState({
    degreeInterest: prefill.degreeInterest,
    areaOfStudy: prefill.areaOfStudy || categoryData?.focusValue || '',
    specialization: prefill.specialization
  });
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  // API state
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
  };
  const generalSpec = categoryData?.focusValue ? getSpecializationsForFocus(categoryData.focusValue).find(s => s.type.toLowerCase().startsWith('general') || s.type.toLowerCase().startsWith('all')) : undefined;
  // Fetch results from API
  const fetchResults = useCallback(async (degreeInterest: string, areaStudy: string, mProgram: string) => {
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudy || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, []);
  // Auto-fetch on page load. URL context wins, so the listings match the
  // program named in the headline rather than the whole category.
  useEffect(() => {
    const area = prefill.areaOfStudy || categoryData?.focusValue;
    if (!area) return;
    fetchResults(prefill.degreeInterest, area, prefill.specialization || generalSpec?.value || '');
  }, [categoryData?.focusValue, fetchResults, generalSpec?.value, prefill]);
  // On mobile the search card sits above the listings, so a new search would
  // otherwise leave the visitor looking at the form they just submitted.
  const resultsRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  const handleSidebarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm()) {
      pendingScrollRef.current = true;
      fetchResults(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  useEffect(() => {
    if (isLoading || !pendingScrollRef.current) return;
    const target = resultsRef.current;
    pendingScrollRef.current = false;
    if (!target || window.innerWidth >= 1024) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const raf = requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [isLoading]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category]);
  if (!categoryData) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-1322" data-mp="172piksxezooz">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1323" data-mp="123lbq8xyq31t">
          Category Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-1324" data-mp="x5jsno0je3qc">
          We couldn't find the degree category you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1325" data-mp="t2esvt7ucg5e">
          Return Home
        </Link>
      </div>;
  }
  return <main className="flex-grow bg-light pb-16" data-id="element-1326" data-mp="egzi3pyjxysz">
      {/* Page Header */}
      <div className="hero-bg text-white py-12" data-id="element-1327" data-mp="ej3xp55tcm93">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1328" data-mp="ije24ddyj4lu">
          {/* Breadcrumbs */}
          <nav className="flex text-sm text-gray-300 mb-6" aria-label="Breadcrumb" data-id="element-1329" data-mp="4jcywra7zl37">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-1330" data-mp="9h4ubva2ppc5">
              <li className="inline-flex items-center" data-id="element-1331" data-mp="x78vlvzshczz">
                <Link to="/" className="inline-flex items-center hover:text-white transition-colors" data-id="element-1332" data-mp="x7r1lkc975l3">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-1333" data-mp="125sko59oj4wk" />
                  Home
                </Link>
              </li>
              <li data-id="element-1334" data-mp="nfbhohhec6xk">
                <div className="flex items-center" data-id="element-1335" data-mp="sgipggjjh648">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1336" data-mp="sf7lafm6lwcl" />
                  <span className="ml-1 md:ml-2" data-id="element-1337" data-mp="1g5l7hpi350z9">Degrees</span>
                </div>
              </li>
              <li aria-current="page" data-id="element-1338" data-mp="1kxbnoc3peiyl">
                <div className="flex items-center" data-id="element-1339" data-mp="1kye9w6q2zx86">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1340" data-mp="35mp3d8b1me02" />
                  <span className="ml-1 md:ml-2 text-white font-medium" data-id="element-1341" data-mp="35l46vd564kzk">
                    {prefill.programLabel || categoryData.name}
                  </span>
                </div>
              </li>
            </ol>
          </nav>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" data-id="element-1342" data-mp="3ae71lolom0hc">
            {headline}
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl" data-id="element-1343" data-mp="2mpe3pmli1ik3">{subhead}</p>
        </div>
      </div>

      <TrustSignals data-id="element-1344" data-mp="2hqbb51mph4t0" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1345" data-mp="2hqujbqmk0etu">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1346" data-mp="2vpaym5yhfdnl">
          {/* Main Content - Listings */}
          <div ref={resultsRef} id="school-matches" className="lg:w-3/4 order-2 lg:order-1 scroll-mt-20" data-id="element-1347" data-mp="2qpojts309pco">
            {/* Loading State */}
            {isLoading && <div className="flex flex-col items-center justify-center py-16" data-id="element-1348" data-mp="b0mxk566isgb">
                <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-1349" data-mp="5zh4u7cir56s" />
                <p className="text-gray-600 font-medium" data-id="element-1350" data-mp="q34tdalsfsrd">
                  Finding your best school matches...
                </p>
              </div>}

            {/* Error State */}
            {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-1351" data-mp="z0yr8pz7augn">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1352" data-mp="13ut8lmrc2xck" />
                <div data-id="element-1353" data-mp="18rjni6rejmwe">
                  <p className="text-red-800 font-medium" data-id="element-1354" data-mp="18ufk9rrik5j9">{apiError}</p>
                  <p className="text-red-600 text-sm mt-1" data-id="element-1355" data-mp="1dq4jtv3j9mfk">
                    Try adjusting your search criteria or check back later.
                  </p>
                </div>
              </div>}

            {/* Results */}
            {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-1356" data-mp="3kixbcgnbu8w4">
                {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-1357" data-mp="3oixo7wus2c12" data-mpi={idx}>
                    <MatchedSchoolCard school={school} index={idx} data-id="element-1358" data-mp="3h0hphfocpmzf" />
                    {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-1359" data-mp="3c8zmyn6ojecc" />}
                  </Fragment>)}
              </div>}

            {/* Empty State */}
            {!isLoading && !apiError && hasSearched && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-1360" data-mp="33932yyv8j3nm">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-1361" data-mp="3ty2fk9uy2m3">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-1362" data-mp="3v0sgqjtkp3l4" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1363" data-mp="3qxck3nhi738f">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-1364" data-mp="3lyjaw7991ye2">
                    Try adjusting your degree level or program selection in the
                    sidebar to find more results.
                  </p>
                </div>}
          </div>

          {/* Sidebar - Form */}
          <div className="lg:w-1/4 order-1 lg:order-2" data-id="element-1365" data-mp="pptwuuj40o8b">
            <div className="sticky top-24" data-id="element-1366" data-mp="kq7jml3a77zi">
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6" data-id="element-1367" data-mp="kr8t4l4nfmf4">
                <div className="bg-navy px-6 py-4" data-id="element-1368" data-mp="yix4unazk8vk">
                  <h3 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-1369" data-mp="tqvvb957cw98">
                    Get Matched Fast
                  </h3>
                  <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1370" data-mp="18hmretdtuhi5">
                    Find the right program for you
                  </p>
                </div>

                <form onSubmit={handleSidebarSubmit} className="p-5 space-y-4" data-id="element-1371" data-mp="1hex2admqg4mj">
                  {/* Degree Level */}
                  <div data-id="element-1372" data-mp="1dfeglfn37pin">
                    <select id="sidebar-degreeInterest" value={formData.degreeInterest} onChange={e => updateFormData('degreeInterest', e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1373" data-mp="1dcs7hbkjt1yb">
                      <option value="" data-id="element-1374" data-mp="395h8sa3e5rd5">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1375" data-mp="2vaf3np6dc61e">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.degreeInterest && <p className="mt-1 text-xs text-red-600" data-id="element-1376" data-mp="2lgetva056cmc">
                        {errors.degreeInterest}
                      </p>}
                  </div>

                  {/* Area of Study */}
                  <div data-id="element-1377" data-mp="2cfqeaav3o47c">
                    <select id="sidebar-areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1378" data-mp="2hc76bg5f72pp">
                      <option value="" data-id="element-1379" data-mp="94v6xe4uox86">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1380" data-mp="977x2ekridwb">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.areaOfStudy && <p className="mt-1 text-xs text-red-600" data-id="element-1381" data-mp="3ve68ji7inthd">
                        {errors.areaOfStudy}
                      </p>}
                  </div>

                  {/* Specialization / Program */}
                  <div data-id="element-1382" data-mp="rxpcjhgl3czq">
                    <select id="sidebar-specialization" value={formData.specialization} onChange={e => updateFormData('specialization', e.target.value)} disabled={!formData.areaOfStudy} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1383" data-mp="n4cy205mcmkp">
                      <option value="" data-id="element-1384" data-mp="1n3wyhxa39zhf">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-1385" data-mp="1x2wanpu2cd96">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.specialization && <p className="mt-1 text-xs text-red-600" data-id="element-1386" data-mp="262srx6e4i9u7">
                        {errors.specialization}
                      </p>}
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full bg-cta hover:bg-cta-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" data-id="element-1387" data-mp="2aw413e6er2pa">
                    {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1388" data-mp="3mi59zp5oatjq" />}
                    {isLoading ? 'Searching...' : 'Search Programs'}
                  </button>
                </form>
              </div>

              {/* Lead capture. The card above filters the listings; this one
                  submits the visitor to the partner APIs, carrying whatever
                  program context is currently selected. */}
              <div className="mb-6" data-id="element-1389" data-mp="33kuca8zow86e">
                <HomeInlineLeadCapture degreeInterest={formData.degreeInterest} areaStudy={formData.areaOfStudy || categoryData.focusValue} mProgram={formData.specialization || generalSpec?.value || ''} title="Get Matched Free" subtitle="Accredited schools, no obligation" data-id="element-1390" data-mp="33lx5inkhimph" />
              </div>

              {/* Undecided nudge — for visitors who can't fill the form yet */}
              <Link to="/degrees" className="group flex items-center gap-3 bg-light hover:bg-white border border-gray-200 rounded-lg px-4 py-3 mb-6 transition-colors duration-200" data-id="element-1391" data-mp="munnt79ktpnx">
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-white group-hover:bg-light border border-gray-200 flex items-center justify-center text-navy transition-colors duration-200" data-id="element-1392" data-mp="zekjx9za3j1e">
                  <UsersIcon className="w-4 h-4" aria-hidden="true" data-id="element-1393" data-mp="qdw4qby5fcmx" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-1394" data-mp="1e3qhktigx176">
                  <span className="block text-sm font-bold text-navy leading-tight" data-id="element-1395" data-mp="1e51lquftsayt">
                    Not sure what to study?
                  </span>
                  <span className="block text-xs text-gray-600 mt-0.5 leading-tight" data-id="element-1396" data-mp="14c2uf4sy26bi">
                    Explore top programs by career
                  </span>
                </span>
                <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-200" aria-hidden="true" data-id="element-1397" data-mp="1x19ptux3qfsj" />
              </Link>

              {/* Related Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1398" data-mp="2bmxf1ui5wai9">
                <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1399" data-mp="26q70jbm09myy">
                  Programs in {categoryData.name}
                </h4>
                <ul className="space-y-2" data-id="element-1400" data-mp="1xoh6rz4niby0">
                  {getSpecializationsForFocus(formData.areaOfStudy || categoryData.focusValue).map(spec => <li key={spec.value} data-id="element-1401" data-mp="1spnwefknv122">
                      <button onClick={() => {
                    updateFormData('specialization', spec.value);
                    fetchResults(formData.degreeInterest, formData.areaOfStudy || categoryData.focusValue, spec.value);
                  }} className={`text-sm flex items-center w-full text-left ${formData.specialization === spec.value ? 'text-crimson font-semibold' : 'text-gray-600 hover:text-crimson'}`} data-id="element-1402" data-mp="1srh13pbprb7f">
                        <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-1403" data-mp="14ztf9ltb6qc0" />
                        {spec.type}
                      </button>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProgramContentSection degreeInterest={formData.degreeInterest} areaOfStudy={formData.areaOfStudy || categoryData.focusValue} specialization={formData.specialization || generalSpec?.value || ''} data-id="element-1404" data-mp="3lkf1s9kxla4y" />
    </main>;
}